import { useEffect, useState } from 'react'
import supabase from '../supabaseClient'
import { readDraft } from '../components/signup/signupState'
import { fetchManagerCommunitySetupMode } from '../lib/communitySetupMode'

/**
 * Read setup-mode + persona state.
 *
 * Community setup mode is sourced from communities.setup_mode (DB).
 * SessionStorage draft fields remain for activation-call progress and
 * signup resume until fully migrated.
 *
 * Returns:
 *   {
 *     isSetupMode  — bool (communities.setup_mode === true)
 *     persona      — 'community' | 'academy' | 'venue' | null
 *     orgName      — string from Screen 2 (or '')
 *     activationCallBooked — bool (legacy flag + convenience)
 *     activationState — 'awaiting_booking' | 'call_booked' | 'call_completed'
 *     activationCallBookedAt — ISO string (or '')
 *     activationCallCompletedAt — ISO string (or '')
 *     refresh()    — re-read from storage + Supabase
 *   }
 */
export default function useSetupMode() {
  const [tick, setTick] = useState(0)
  const draft = readDraft()
  const [dbSetupMode, setDbSetupMode] = useState(null)

  let activationState = 'awaiting_booking'
  if (draft.activationCallCompletedAt) activationState = 'call_completed'
  else if (draft.activationCallBookedAt || draft.activationCallBooked)
    activationState = 'call_booked'

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (cancelled) return
        if (!user?.id) {
          setDbSetupMode(null)
          return
        }
        const { setupMode } = await fetchManagerCommunitySetupMode(user.id)
        if (!cancelled) setDbSetupMode(setupMode)
      } catch (err) {
        console.warn('useSetupMode: failed to load setup_mode', err)
        if (!cancelled) setDbSetupMode(null)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [tick])

  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key || e.key.startsWith('statbox.signupDraft')) {
        setTick((t) => t + 1)
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const isSetupMode =
    draft.persona === 'community'
      ? false
      : dbSetupMode !== null
        ? dbSetupMode
        : draft.accountStatus === 'setup_mode'

  return {
    isSetupMode,
    reviewCompleted: draft.accountStatus === 'review_completed',
    persona: draft.persona || null,
    orgName: draft.organizationName || '',
    fullName: draft.fullName || '',
    activationCallBooked: !!(draft.activationCallBooked || draft.activationCallBookedAt),
    activationState,
    activationCallBookedAt: draft.activationCallBookedAt || '',
    activationCallCompletedAt: draft.activationCallCompletedAt || '',
    refresh: () => setTick((t) => t + 1),
    _tick: tick,
  }
}
